/**
 * Resolves the URL for the "Order Now" button in `StrapiProductHeader.vue`.
 *
 * Owns ONLY the page-header button. Sibling "Order Now" buttons
 * (`OrderConfiguration.vue`, `CustomOrderConfiguration.vue`, …) make
 * deliberately different decisions — `OrderConfiguration.vue` gates on
 * `chargebeePlanId && selectedLocationOrderUrl`, the inverse of the
 * legacy branch below. Do NOT "harmonise" the contracts.
 *
 * URLs are treated as opaque strings. The shape of the BE-generated
 * reset URL (`?dur=&units=&pk=`) comes from
 * `graph/model/utils.go::GenerateOrderURL` and is not validated here —
 * or, today, by any other test. A BE change that drops `dur=` or
 * `units=` would not trip these tests.
 */

/** Deep link used for Microsoft-Store-gated products (Xbox UWP titles). */
export const MICROSOFT_STORE_URL =
  'https://www.microsoft.com/en-us/p/gportal-game-servers/9nm39brp1dd2';

export type ResolveHeaderOrderNowUrlInput = {
  microsoftStore: boolean;
  /** BE-generated default URL; `null` when pricing data is unavailable. */
  defaultOrderUrl: string | null;
  /** Presence discriminates Chargebee from legacy products. */
  chargebeePlanId: string | null;
  /** `orderUrl` of the locationStore's selection; unset before any tile click. */
  selectedLocationOrderUrl: string | null | undefined;
};

/** Returns the URL to link to, or `null` when none can be resolved. */
export function resolveHeaderOrderNowUrl(
  opts: ResolveHeaderOrderNowUrlInput,
): string | null {
  if (opts.microsoftStore) {
    return MICROSOFT_STORE_URL;
  }

  if (opts.defaultOrderUrl == null) {
    return null;
  }

  // Chargebee products must ignore the locationStore override: the header
  // click means "reset to product defaults", and honouring a previously
  // clicked tile's URL would re-apply that selection. Only legacy products
  // honour the override (their checkout needs per-location URLs); empty/
  // whitespace overrides are treated as absent — `href=""` would hide the
  // button.
  const locationOverride = opts.selectedLocationOrderUrl?.trim();
  if (!opts.chargebeePlanId && locationOverride) {
    return locationOverride;
  }

  return opts.defaultOrderUrl;
}
